import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AppDispatch, RootState } from '../../store';
import { fetchTasks } from '../../store/slices/tasksSlice';
import LookAheadFilterBar from '../../components/dashboard/LookAheadFilterBar';
import LookAheadSummaryTiles from '../../components/dashboard/LookAheadSummaryTiles';
import LookAheadTaskList from '../../components/dashboard/LookAheadTaskList';
import ResourceRollupPanel from '../../components/dashboard/ResourceRollupPanel';
import CrewDemandWidget from '../../components/dashboard/CrewDemandWidget';
import BlockersPanel from '../../components/dashboard/BlockersPanel';
import ProcurementPipelineWidget from '../../components/dashboard/ProcurementPipelineWidget';
import DailyRecordScaffoldPanel from '../../components/dashboard/DailyRecordScaffoldPanel';
import PermitLookAheadPanel from '../../components/dashboard/PermitLookAheadPanel';
import resourcesService, {
  ResourceRollupRow,
} from '../../services/api/resourcesService';
import type { ResourceStatusBreakdown } from '../../types/procurement';
import type { Task } from '../../services/api/tasksService';

function AttentionList({
  title,
  tasks,
  emptyText,
}: {
  title: string;
  tasks: Task[];
  emptyText: string;
}) {
  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Chip size="small" label={tasks.length} color={tasks.length > 0 ? 'warning' : 'success'} />
      </Stack>
      {tasks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {tasks.slice(0, 4).map((task) => (
            <Box key={task.id} sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {task.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {task.phase?.name ?? task.team?.name ?? 'No phase/team'} · Due {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'not set'}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default function PMDashboardPage() {
  const dispatch = useDispatch() as AppDispatch;
  const { filters } = useSelector((s: RootState) => s.pmDashboard);
  const { tasks, loading: tasksLoading, error } = useSelector(
    (s: RootState) => s.tasks
  );

  const [rollup, setRollup] = useState<ResourceRollupRow[]>([]);
  const [statusBreakdown, setStatusBreakdown] = useState<ResourceStatusBreakdown[]>([]);
  const [rollupLoading, setRollupLoading] = useState(false);

  useEffect(() => {
    dispatch(
      fetchTasks({
        startDate: filters.range.start,
        endDate: filters.range.end,
        status: filters.status,
        teamId: filters.teamId,
        projectId: filters.projectId,
        phaseId: filters.phaseId,
        blockId: filters.blockId,
        buildingId: filters.buildingId,
        unitId: filters.unitId,
        limit: 100,
      })
    );
  }, [dispatch, filters]);

  useEffect(() => {
    if (tasks.length === 0) {
      setRollup([]);
      setStatusBreakdown([]);
      return;
    }
    let cancelled = false;
    setRollupLoading(true);
    resourcesService
      .getRollup(tasks.map((t) => t.id))
      .then((rows) => {
        if (cancelled) return;
        setRollup(rows);
        setStatusBreakdown(
          rows.map((r) => ({
            resourceId: r.resourceId,
            onSite: r.quantityAllocated,
            inTransit: 0,
            ordered: 0,
            notOrdered: r.procurementEligible ? Math.max(0, r.quantityRequired - r.quantityAllocated) : 0,
          }))
        );
      })
      .catch(() => {
        if (cancelled) return;
        setRollup([]);
        setStatusBreakdown([]);
      })
      .finally(() => {
        if (!cancelled) setRollupLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tasks]);

  const now = new Date();
  const inSevenDays = new Date(now);
  inSevenDays.setDate(now.getDate() + 7);

  const blockedTasks = tasks.filter((task) => (
    task.lifecycleStatus?.toLowerCase() === 'blocked'
    || task.status?.toLowerCase() === 'blocked'
    || Boolean(task.blockedReason)
  ));
  const atRiskTasks = tasks.filter((task) => {
    if (!task.dueDate || blockedTasks.some((blocked) => blocked.id === task.id)) return false;
    const dueDate = new Date(task.dueDate);
    return dueDate <= inSevenDays && task.percentComplete < 75 && task.status !== 'COMPLETED';
  });
  const needsDatesTasks = tasks.filter((task) => !task.startDate || !task.dueDate);
  const readyTasks = tasks.filter((task) => task.materialsReady === true && task.status !== 'COMPLETED');

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            PM Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This week's action queue first; supporting resource and procurement detail below.
          </Typography>
        </Box>
      </Stack>

      <LookAheadFilterBar />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load tasks: {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <LookAheadSummaryTiles tasks={tasks} />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <LookAheadTaskList tasks={tasks} loading={tasksLoading} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <Box sx={{ p: 2.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                PM Attention
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Keep this panel to exceptions and decisions only.
              </Typography>
            </Box>
            <AttentionList
              title="Blocked Now"
              tasks={blockedTasks}
              emptyText="No blocked tasks in this look-ahead window."
            />
            <AttentionList
              title="At Risk This Week"
              tasks={atRiskTasks}
              emptyText="No near-term tasks are currently at risk."
            />
            <AttentionList
              title="Needs Dates"
              tasks={needsDatesTasks}
              emptyText="Every visible task has start and due dates."
            />
            <PermitLookAheadPanel
              projectId={filters.projectId || undefined}
              startDate={filters.range.start}
              endDate={filters.range.end}
            />
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Readiness Detail
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} lg={7}>
          <Stack spacing={2}>
            <ResourceRollupPanel
              rows={rollup}
              status={statusBreakdown}
              loading={rollupLoading}
            />
            <CrewDemandWidget tasks={tasks} />
          </Stack>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Stack spacing={2}>
            <DailyRecordScaffoldPanel
              projectId={filters.projectId || undefined}
              defaultDate={filters.range.start}
            />
            <BlockersPanel tasks={tasks} />
            <ProcurementPipelineWidget projectId={filters.projectId || undefined} />
            <Box sx={{ p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Ready to Release
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {readyTasks.length} task{readyTasks.length === 1 ? '' : 's'} have materials marked ready and can be pulled into the field plan.
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
